import { useState, useEffect } from "react";
import { api, InventoryStats, ApiError } from "../api";
import { GlassPanel, KpiCard, toast } from "../components";

const PIN_MIN_LEN = 4;
const PIN_MAX_LEN = 6;
const DEFAULT_UNLOCK_MINUTES = 5;

/**
 * PIN ca hàng — đặt / đổi từ web dashboard.
 * PIN là HÀNG RÀO HẠN CHẾ (deterrent) chặn thao tác nhầm/vặt, KHÔNG phải kiểm soát quyền.
 * Đặt PIN mới KHÔNG cần PIN cũ: user dashboard đã đăng nhập → đây là đường khôi phục khi quên PIN.
 * Trên máy Android, PIN chỉ gác 2 việc: mở ngăn kéo tiền thủ công và vào Cài đặt. Đổi giá KHÔNG cần PIN.
 */
function PosPinSection() {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // null = endpoint chưa sẵn sàng (worker chưa ship) → UI vẫn hiện, chỉ báo chờ.
  const [pinSet, setPinSet] = useState<boolean | null>(null);
  const [windowMinutes, setWindowMinutes] = useState<string>(String(DEFAULT_UNLOCK_MINUTES));

  const [pin, setPin] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");
  const [savingPin, setSavingPin] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);
  const [savingWindow, setSavingWindow] = useState(false);

  const endpointMissing = (e: unknown) => e instanceof ApiError && (e.status === 404 || e.status === 405);

  useEffect(() => {
    let alive = true;
    api.pos
      .getSettings()
      .then((s) => {
        if (!alive) return;
        setPinSet(s.pin_set);
        setWindowMinutes(String(s.unlock_window_minutes ?? DEFAULT_UNLOCK_MINUTES));
      })
      .catch((e: unknown) => {
        if (!alive) return;
        if (endpointMissing(e)) {
          setPinSet(null); // seam chưa sẵn sàng — không chặn UI
        } else {
          setLoadError((e as Error).message || "Không tải được cấu hình PIN");
        }
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const pinInvalid = (v: string) =>
    !/^\d+$/.test(v) ? `PIN chỉ gồm chữ số.` :
    v.length < PIN_MIN_LEN || v.length > PIN_MAX_LEN ? `PIN phải từ ${PIN_MIN_LEN}–${PIN_MAX_LEN} chữ số.` :
    null;

  const handleSetPin = async () => {
    setPinError(null);
    const bad = pinInvalid(pin);
    if (bad) { setPinError(bad); return; }
    if (pin !== pinConfirm) { setPinError("Xác nhận PIN không khớp."); return; }
    setSavingPin(true);
    try {
      await api.pos.setPin(pin);
      setPinSet(true);
      setPin("");
      setPinConfirm("");
      toast.success(pinSet ? "Đã đổi PIN ca hàng" : "Đã đặt PIN ca hàng");
    } catch (e: unknown) {
      if (endpointMissing(e)) toast.warning("Endpoint PIN chưa sẵn sàng trên server");
      else setPinError((e as Error).message || "Lỗi đặt PIN");
    } finally {
      setSavingPin(false);
    }
  };

  const handleSaveWindow = async () => {
    const n = Number(windowMinutes);
    if (!Number.isFinite(n) || n < 1 || n > 60) {
      toast.warning("Thời lượng mở khoá phải từ 1–60 phút");
      return;
    }
    setSavingWindow(true);
    try {
      const r = await api.pos.updateSettings({ unlock_window_minutes: Math.round(n) });
      setWindowMinutes(String(r.unlock_window_minutes));
      toast.success("Đã lưu thời lượng mở khoá");
    } catch (e: unknown) {
      if (endpointMissing(e)) toast.warning("Endpoint cài đặt chưa sẵn sàng trên server");
      else toast.error((e as Error).message || "Lỗi lưu thời lượng mở khoá");
    } finally {
      setSavingWindow(false);
    }
  };

  return (
    <GlassPanel title="PIN ca hàng" badge="deterrent" badgeVariant="info" style={{ marginTop: 16 }}>
      <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 4 }}>
        PIN gác 2 thao tác trên máy POS: <strong>mở ngăn kéo tiền thủ công</strong> và <strong>vào Cài đặt</strong>.
        Đổi giá bán <strong>không</strong> cần PIN.
      </p>
      <p style={{ fontSize: 12, color: "var(--text-tertiary)", marginBottom: 12 }}>
        PIN chỉ để hạn chế thao tác nhầm hoặc nghịch tay — không phải lớp bảo vệ chống người trong cuộc.
        Đặt PIN ở đây (hoặc trong Telegram) đều được; nhập sai PIN thì máy sẽ báo cho chủ shop qua Telegram.
      </p>

      {loading ? (
        <div style={{ fontSize: 13, color: "var(--text-tertiary)" }}>Đang tải cấu hình…</div>
      ) : loadError ? (
        <div className="alert alert-error" role="alert">
          <span className="material-symbols-outlined">error</span> {loadError}
        </div>
      ) : (
        <>
          {pinSet === null && (
            <div role="status" style={{ marginBottom: 12, padding: "8px 12px", borderRadius: 8, background: "var(--status-amber-bg)", border: "1px solid var(--status-amber-border)", color: "var(--status-amber)", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>schedule</span>
              Máy chủ chưa có endpoint PIN — form dưới đây đã sẵn, sẽ hoạt động ngay khi worker cập nhật.
            </div>
          )}
          <p style={{ fontSize: 12, marginBottom: 12 }}>
            Trạng thái PIN:{" "}
            {pinSet === null
              ? <em style={{ color: "var(--text-tertiary)" }}>chưa xác định</em>
              : pinSet
                ? <strong style={{ color: "var(--status-amber)" }}>đã đặt</strong>
                : <strong style={{ color: "var(--text-secondary)" }}>chưa đặt</strong>}
          </p>

          <div className="form-group">
            <label htmlFor="pos-pin-new">{pinSet ? "PIN mới" : "Đặt PIN"}</label>
            <input
              id="pos-pin-new"
              type="password"
              inputMode="numeric"
              autoComplete="new-password"
              className="input"
              placeholder={`${PIN_MIN_LEN}–${PIN_MAX_LEN} chữ số`}
              value={pin}
              maxLength={PIN_MAX_LEN}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              style={{ maxWidth: 220, letterSpacing: 2 }}
            />
            <div className="form-hint">
              Đặt PIN mới không cần PIN cũ — dùng khi quên PIN.
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="pos-pin-confirm">Xác nhận PIN mới</label>
            <input
              id="pos-pin-confirm"
              type="password"
              inputMode="numeric"
              autoComplete="new-password"
              className="input"
              placeholder="Nhập lại PIN"
              value={pinConfirm}
              maxLength={PIN_MAX_LEN}
              onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => e.key === "Enter" && handleSetPin()}
              style={{ maxWidth: 220, letterSpacing: 2 }}
            />
          </div>

          {pinError && (
            <div className="alert alert-error" role="alert" style={{ marginBottom: 12 }}>
              <span className="material-symbols-outlined">gpp_bad</span> {pinError}
            </div>
          )}

          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSetPin}
            disabled={savingPin || !pin}
          >
            {savingPin
              ? <><span className="spinner" /> Đang lưu…</>
              : <><span className="material-symbols-outlined" style={{ fontSize: 18 }}>lock_reset</span> {pinSet ? "Đổi PIN" : "Đặt PIN"}</>}
          </button>

          <hr style={{ margin: "16px 0", border: 0, borderTop: "1px solid var(--border-subtle)" }} />

          <div className="form-group">
            <label htmlFor="pos-pin-window">Thời lượng mở khoá (phút)</label>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input
                id="pos-pin-window"
                type="number"
                min={1}
                max={60}
                step={1}
                className="input"
                value={windowMinutes}
                onChange={(e) => setWindowMinutes(e.target.value)}
                style={{ maxWidth: 120 }}
              />
              <button
                type="button"
                className="btn btn-outline"
                onClick={handleSaveWindow}
                disabled={savingWindow}
              >
                {savingWindow ? <><span className="spinner" /> Đang lưu…</> : "Lưu"}
              </button>
            </div>
            <div className="form-hint">
              Sau khi nhập đúng PIN, máy POS cho thao tác trong bao nhiêu phút rồi tự khoá lại (mặc định {DEFAULT_UNLOCK_MINUTES}).
            </div>
          </div>
        </>
      )}
    </GlassPanel>
  );
}

export default function AdminToolsPage() {
  // ── Sync state ──
  const [fullSyncLoading, setFullSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [lastSync, setLastSync] = useState<string | null>(null);

  // ── Stats state ──
  const [stats, setStats] = useState<InventoryStats | null>(null);
  const [orderCount, setOrderCount] = useState<number | null>(null);
  const [productCount, setProductCount] = useState<number | null>(null);

  // ── Admin secret ──
  const [secret, setSecret] = useState("");
  const [authStatus, setAuthStatus] = useState<"idle" | "ok" | "fail">("idle");
  const [authLoading, setAuthLoading] = useState(false);

  // ── Load data on mount ──
  useEffect(() => {
    let alive = true;

    // Get last sync time from sync status
    api.syncStatus()
      .then((s) => {
        if (!alive) return;
        setLastSync(s.last_loyverse_sync);
      })
      .catch(() => {});

    // Inventory stats
    api.inventoryStats()
      .then((s) => alive && setStats(s))
      .catch(() => {});

    // Order count (today)
    api.orders({ limit: 1 })
      .then((r) => alive && setOrderCount(r.offset !== undefined ? r.offset : null))
      .catch(() => {});

    // Product count — worker returns {products, limit, offset} without total,
    // so fetch with high limit and count results
    api.products({ limit: 500 })
      .then((r) => alive && setProductCount(r.products?.length ?? null))
      .catch(() => {});

    // Check if admin secret already in sessionStorage
    const saved = sessionStorage.getItem("admin_secret_verified");
    if (saved === "true") setAuthStatus("ok");

    return () => { alive = false; };
  }, []);

  // ── Sync handlers ──
  const handleBackfill30d = async () => {
    setFullSyncLoading(true);
    setSyncResult(null);
    setSyncError(null);
    try {
      const headers = api.authHeaders();
      const r = await fetch(`${api.baseUrl}/api/sync/loyverse?full=1`, { method: "POST", headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json() as any;
      const totalSynced = [data?.stores, data?.cats, data?.items, data?.inv, data?.receipts]
        .reduce((sum: number, r: any) => sum + (r?.rowsUpserted ?? 0), 0);
      setSyncResult(totalSynced > 0
        ? `Backfill 30 ngày hoàn tất. ${totalSynced} bản ghi.`
        : `Backfill 30 ngày hoàn tất ✓`);
      api.syncStatus().then((s) => setLastSync(s.last_loyverse_sync)).catch(() => {});
    } catch (e: any) {
      setSyncError(e.message || "Backfill thất bại");
    } finally {
      setFullSyncLoading(false);
    }
  };

  // ── Auth handler ──
  const handleAuth = async () => {
    if (!secret.trim()) return;
    setAuthLoading(true);
    setAuthStatus("idle");
    try {
      const res = await fetch(`${api.baseUrl}/api/sync/loyverse`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Secret": secret.trim(),
        },
      });
      if (res.ok) {
        setAuthStatus("ok");
        sessionStorage.setItem("admin_secret_verified", "true");
        localStorage.setItem("admin_secret", secret.trim());
      } else {
        setAuthStatus("fail");
      }
    } catch {
      setAuthStatus("fail");
    } finally {
      setAuthLoading(false);
    }
  };

  return (
    <>
      {/* ── Section: Đồng bộ Loyverse ── */}
      <GlassPanel title="Đồng bộ Loyverse" badge="sync" badgeVariant="info">
        <div style={{ marginBottom: 12 }}>
          <span style={{ color: "var(--text-secondary)", fontSize: 13 }}>
            Lần sync gần nhất:{" "}
            <strong style={{ color: "var(--text-primary)" }}>
              {lastSync ? new Date(lastSync).toLocaleString("vi-VN") : "Chưa có"}
            </strong>
          </span>
        </div>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <button
            className="btn btn-outline"
            disabled={fullSyncLoading}
            onClick={handleBackfill30d}
          >
            {fullSyncLoading ? (
              <><span className="spinner" /> Đang chạy...</>
            ) : (
              <><span className="material-symbols-outlined" style={{ fontSize: 18 }}>history</span> Backfill 30 ngày</>
            )}
          </button>
        </div>

        <p style={{ fontSize: 12, color: "var(--text-tertiary)", marginTop: 8 }}>
          <strong>Backfill 30 ngày:</strong> Kéo toàn bộ dữ liệu 30 ngày gần nhất từ Loyverse (dùng khi mới cài hoặc mất data).
        </p>
        <p style={{ fontSize: 12, color: "var(--text-tertiary)", marginTop: 4 }}>
          💡 Đồng bộ nhanh (POS → D1 → Web) đã chuyển sang nút <strong>"Đồng bộ"</strong> trên thanh trên cùng.
        </p>

        {syncResult && (
          <div className="alert alert-ok" style={{ marginTop: 12 }}>
            <span className="material-symbols-outlined">check_circle</span> {syncResult}
          </div>
        )}
        {syncError && (
          <div className="alert alert-error" style={{ marginTop: 12 }}>
            <span className="material-symbols-outlined">error</span> {syncError}
          </div>
        )}
      </GlassPanel>

      {/* ── Section: Thống kê hệ thống ── */}
      <GlassPanel title="Thống kê hệ thống" badge="stats" badgeVariant="info" style={{ marginTop: 16 }}>
        <div className="kpi-grid">
          <KpiCard
            label="Sản phẩm"
            value={productCount !== null ? String(productCount) : "—"}
            note="tổng master items"
            dotColor="blue"
          />
          <KpiCard
            label="Biến thể tồn kho"
            value={stats ? String(stats.variants) : "—"}
            note="variants đang track"
            dotColor="blue"
          />
          <KpiCard
            label="Tồn kho thấp"
            value={stats ? String(stats.low_stock + stats.out_of_stock) : "—"}
            note="cần nhập thêm"
            dotColor={stats && (stats.low_stock + stats.out_of_stock) > 0 ? "warn" : ""}
          />
        </div>
      </GlassPanel>

      {/* ── Section: Cache & KV ── */}
      <GlassPanel title="Cache & KV" badge="readonly" badgeVariant="info" style={{ marginTop: 16 }}>
        <div className="list">
          <div className="list-item">
            <div>
              <div className="name">GCP Token Cache</div>
              <div className="meta">KV cache 50 phút cho Google Cloud token</div>
            </div>
            <div className="right">
              <span className="material-symbols-outlined" style={{ color: "var(--text-tertiary)" }}>lock_clock</span>
            </div>
          </div>
          <div className="list-item">
            <div>
              <div className="name">D1 Cleanup Cron</div>
              <div className="meta">Tự động dọn dẹp dữ liệu cũ lúc 02:00 hàng ngày</div>
            </div>
            <div className="right">
              <span className="material-symbols-outlined" style={{ color: "var(--text-tertiary)" }}>schedule</span>
            </div>
          </div>
          <div className="list-item">
            <div>
              <div className="name">Loyverse Sync Cron</div>
              <div className="meta">Đồng bộ tự động mỗi 5 phút qua Cloudflare Cron Trigger</div>
            </div>
            <div className="right">
              <span className="material-symbols-outlined" style={{ color: "var(--text-tertiary)" }}>autorenew</span>
            </div>
          </div>
        </div>
      </GlassPanel>

      {/* ── Section: Admin Secret (Danger Zone) ── */}
      <GlassPanel title="Admin Secret" badge="danger" badgeVariant="warn" style={{ marginTop: 16 }}>
        <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 12 }}>
          Xác thực quyền admin để thực hiện các thao tác đồng bộ và quản trị.
        </p>

        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input
            type="password"
            className="input"
            placeholder="Nhập admin secret để xác thực"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAuth()}
            style={{ flex: 1, minWidth: 200 }}
          />
          <button
            className="btn btn-primary"
            disabled={authLoading || !secret.trim()}
            onClick={handleAuth}
          >
            {authLoading ? (
              <><span className="spinner" /> Đang xác thực...</>
            ) : (
              "Xác thực"
            )}
          </button>
        </div>

        {authStatus === "ok" && (
          <div className="alert alert-ok" style={{ marginTop: 12 }}>
            <span className="material-symbols-outlined">verified</span> Xác thực OK — quyền admin đã được lưu.
          </div>
        )}
        {authStatus === "fail" && (
          <div className="alert alert-error" style={{ marginTop: 12 }}>
            <span className="material-symbols-outlined">gpp_bad</span> Sai secret hoặc không có quyền admin.
          </div>
        )}
      </GlassPanel>

      {/* ── Section: PIN ca hàng (đặt/đổi từ web) ── */}
      <PosPinSection />
    </>
  );
}
